import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Copy, Hammer } from 'lucide-react';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  AutocompleteMultiSelect,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { readShigoAppConfig } from '../../../../../lib/config';
import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import {
  usePlacementAdUnitConfigQuery,
  usePlacementEditorLookups,
  usePlacementMutations,
} from '../../hooks';
import type { PlacementFormValue } from '../../placementFormSchema';
import { createPlacementUnitTag } from '../../unitTag';
import { copyToClipboard } from '../../../../../lib/clipboard';

const adUnitFieldLabelKeys: Record<string, DashboardMessageKey> = {
  'adUnit.adWidth': 'placements.adWidth',
  'adUnit.adHeight': 'placements.adHeight',
  'adUnit.devicePlacement': 'placements.devicePlacement',
  'adUnit.viewIn': 'placements.viewIn',
  'adUnit.viewOut': 'placements.viewOut',
  'adUnit.adLoadDelay': 'placements.adLoadDelay',
  'adUnit.refreshRate': 'placements.refreshRate',
  'adUnit.maxRefresh': 'placements.maxRefresh',
};

export function PlacementAdUnitFields({
  placementId,
  secondaryId,
}: {
  placementId?: string | undefined;
  secondaryId?: string | undefined;
}) {
  'use no memo';
  const { control, register, formState } = useFormContext<PlacementFormValue>();
  const publisherId = useWatch({ control, name: 'publisherId' });
  const lookups = usePlacementEditorLookups(publisherId, true);
  const configQuery = usePlacementAdUnitConfigQuery(placementId, Boolean(placementId));
  const mutations = usePlacementMutations();
  const config = readShigoAppConfig();
  const tag = createPlacementUnitTag(secondaryId, config.cdnUrl);
  return (
    <DashboardPageFormBody width="lg">
      <div className="grid gap-5 md:grid-cols-2">
        <DashboardPageFormField
          label={dashboardT('placements.adUnitName')}
          htmlFor="placement-ad-unit-name"
        >
          <Input id="placement-ad-unit-name" {...register('adUnit.name')} />
        </DashboardPageFormField>
        <Controller
          control={control}
          name="adUnit.widgetName"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('placements.widget')}
              required
              error={fieldState.error?.message}
            >
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger aria-invalid={Boolean(fieldState.error)}>
                  <SelectValue placeholder={dashboardT('placements.selectWidget')} />
                </SelectTrigger>
                <SelectContent>
                  {(lookups.widgets.data?.content ?? []).map((widget) => (
                    <SelectItem key={widget.id} value={widget.displayName}>
                      {widget.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="adUnit.publisherDomainIds"
          render={({ field }) => (
            <DashboardPageFormField label={dashboardT('placements.publisherDomains')}>
              <AutocompleteMultiSelect
                label={dashboardT('placements.publisherDomains')}
                options={(lookups.domains.data?.content ?? []).map((domain) => ({
                  id: domain.id,
                  label: domain.domain,
                }))}
                value={field.value}
                onValueChange={field.onChange}
                loading={lookups.domains.isFetching}
                placeholder={dashboardT('placements.publisherDomains')}
                emptyMessage={dashboardT('placements.noPublisherDomains')}
              />
            </DashboardPageFormField>
          )}
        />
        {(
          [
            'adUnit.adWidth',
            'adUnit.adHeight',
            'adUnit.devicePlacement',
            'adUnit.viewIn',
            'adUnit.viewOut',
            'adUnit.adLoadDelay',
            'adUnit.refreshRate',
            'adUnit.maxRefresh',
          ] as const
        ).map((name) => {
          let error: string | undefined;
          if (name === 'adUnit.adWidth') error = formState.errors.adUnit?.adWidth?.message;
          if (name === 'adUnit.adHeight') error = formState.errors.adUnit?.adHeight?.message;
          return (
            <DashboardPageFormField
              key={name}
              label={dashboardT(adUnitFieldLabelKeys[name] ?? 'placements.name')}
              error={error}
            >
              <Input
                type={
                  name === 'adUnit.devicePlacement' ||
                  name === 'adUnit.viewIn' ||
                  name === 'adUnit.viewOut'
                    ? 'text'
                    : 'number'
                }
                min="0"
                {...register(name)}
              />
            </DashboardPageFormField>
          );
        })}
      </div>
      <section className="border-input grid gap-3 rounded-md border p-4">
        <h2 className="text-sm font-semibold">{dashboardT('placements.unitSettings')}</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {(
            [
              ['adUnit.video', 'placements.video'],
              ['adUnit.s2s', 'placements.s2s'],
              ['adUnit.bidder', 'placements.bidder'],
              ['adUnit.closeButton', 'placements.closeButton'],
              ['adUnit.adhesion', 'placements.adhesion'],
              ['adUnit.noFillHideUnit', 'placements.noFillHideUnit'],
              ['adUnit.idSystemIdentityLink', 'placements.identityLink'],
              ['adUnit.ibv', 'placements.ibv'],
              ['adUnit.weMakeSticky', 'placements.sticky'],
            ] as const
          ).map(([name, label]) => (
            <Controller
              key={name}
              control={control}
              name={name}
              render={({ field }) => (
                <Label className="flex items-center gap-2 text-sm font-normal">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked === true);
                    }}
                  />
                  {dashboardT(label)}
                </Label>
              )}
            />
          ))}
        </div>
      </section>
      {placementId ? (
        <section className="border-input grid gap-3 rounded-md border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={mutations.buildAdUnit.isPending}
              onClick={() => {
                void mutations.buildAdUnit
                  .mutateAsync(placementId)
                  .then(() => toast.success(dashboardT('placements.buildSuccess')))
                  .catch((error: unknown) => {
                    showRequestError(error, dashboardT('placements.buildError'));
                  });
              }}
            >
              <Hammer className="h-4 w-4" aria-hidden="true" />
              {dashboardT('placements.build')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!tag}
              onClick={() => {
                void copyToClipboard(tag, {
                  successMessage: dashboardT('placements.copied'),
                  errorMessage: dashboardT('placements.copyError'),
                });
              }}
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              {dashboardT('placements.copyUnitTag')}
            </Button>
          </div>
          <DashboardPageFormField label={dashboardT('placements.unitTag')}>
            <Textarea readOnly rows={2} value={tag} />
          </DashboardPageFormField>
          <DashboardPageFormField label={dashboardT('placements.configJson')}>
            <Textarea
              readOnly
              rows={8}
              value={
                configQuery.isError
                  ? dashboardT('placements.configError')
                  : JSON.stringify(configQuery.data ?? {}, null, 2)
              }
            />
          </DashboardPageFormField>
        </section>
      ) : null}
    </DashboardPageFormBody>
  );
}
